import type { ContentMode } from '@confluence/content-types-utils/entry-points/getContentMode.ts';
import type {
	ContentPermissionCapabilitySurface,
	ContentPermissionResult,
} from '@confluence/share-and-restrict-dialog/entry-points/ContentPermissionResult.ts';

type ContentOperation = {
	readonly operation: string | null;
	readonly targetType: string | null;
} | null;

type GetContentPermissionResultArgs = {
	contentType: string | undefined;
	hasError: boolean;
	isLoading: boolean;
	operations: ReadonlyArray<ContentOperation> | null | undefined;
};

export const getContentPermissionCapabilitySurface = (
	contentMode: ContentMode | undefined,
	contentType: string | undefined,
): ContentPermissionCapabilitySurface | undefined => {
	if (contentMode === 'view' || contentMode === 'archived') {
		return contentMode;
	}

	if (contentType === 'embed') {
		return 'embed';
	}

	return undefined;
};

/**
 * Reuse permissions already loaded by the header. If unavailable, keep the existing request
 * behavior.
 */
export const getContentPermissionResult = ({
	contentType,
	hasError,
	isLoading,
	operations,
}: GetContentPermissionResultArgs): ContentPermissionResult => {
	if (hasError) {
		return {
			status: 'unavailable',
			unavailableReason: 'content_operations_error',
		};
	}

	if (isLoading) {
		return { status: 'loading' };
	}

	if (!contentType) {
		return {
			status: 'unavailable',
			unavailableReason: 'content_type_missing',
		};
	}

	if (!operations) {
		return {
			status: 'unavailable',
			unavailableReason: 'content_operations_missing',
		};
	}

	return {
		status: operations.some(
			(operation) =>
				operation?.operation === 'restrict_content' && operation.targetType === contentType,
		)
			? 'allowed'
			: 'denied',
	};
};
